/**
 * ============================================================================
 * ProgramDetailsOpenRfi — Controller (client)
 * ============================================================================
 * Purpose: Own form state (via react-hook-form), validation, lead-check
 * debounce, lead submission, OTP orchestration, analytics and storage side
 * effects for the three ProgramDetailsOpenRfi views:
 *   - components/organisms/NewBannerForm/ProgramDetailsOpenRfi.tsx
 *   - components/sections/NewBannerFormLp/ProgramDetailsOpenRfi.tsx
 *   - components/organisms/modal/ConnectModalMba/ProgramDetailsOpenRfiConnectModal.tsx
 *
 * No JSX — each view consumes the returned ViewModel and stays UI-only.
 * react-hook-form (formState.isSubmitting / isValid) is the single source of
 * truth for form + submission state; there is no parallel `useState` for
 * form values or field errors.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import {
  setLeadFormDegreeValue,
  setLeadFormProgramValue,
  setLeadFormInputValue,
} from "@/helpers/leadFormDegreeProgramFields";
import { scheduleAfterPaint } from "@/helpers/scheduleAfterPaint";

import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { usePathname, useSearchParams } from "next/navigation";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { pushToDataLayer, pushToDataLayerDeferred } from "@/helpers/gtag";
import { StorageService } from "@/helpers/storage.service";
import { getGlobalStateValue } from "@/context/utils";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import { fetchIpInfoData } from "@/context/ip.storage.utils";
import {
  filterSelectedPrograms,
  formatDate,
  getProgramId,
  getSpecializationValue,
  precedentSelectedPrograms,
} from "../utils";
import {
  checkExistingLead,
  createLead,
  createProspectActivity,
  getOrCreateUniqueLeadId,
} from "../service/programDetailsOpenRfi.service";
import { normalizeLeadResponseForOtpDisplay } from "@/components/organisms/modal/ConnectModal/utils";
import {
  regexForInputs,
  SELECT_DEGREE,
  SELECT_PROGRAM,
  SELECT_PMI_PATHWAY,
  createProgramDetailsOpenRfiResolver,
  type ProgramDetailsOpenRfiFormValues,
} from "../model/programDetailsOpenRfi.types";
import { PMI_ROUTES } from "@/data/pmi-utils";
import { decryptionJiopc } from "@/components/molecules/CryptoJs/Helpers";
import { jwtDecode } from "jwt-decode";

const LEAD_CHECK_DEBOUNCE_MS = 2000;
const IP_INFO_DELAY_MS = 1000;

export type LeadCreateEndpoint = "lead-create-new" | "lead-create-lp-new" | "jiopc-lead-create-new";

export interface UseProgramDetailsOpenRfiControllerOptions {
  ProgramId?: any;
  associatedPrograms: Array<ProgramDetails>;
  /** Landing-page variant shows degree/program dropdowns and filters programsList. */
  isLandingPage?: boolean;
  /** Shows the PMI Pathway dropdown and requires a selection before submit. */
  isPmi?: boolean;
  /** Affiliate page defaults country code to +971 and shows the existing-lead popup. */
  isAffiliatePage?: boolean;
  isLambdaAPI?: boolean;
  isJobRole?: boolean;
  isScholarShipPage?: boolean;
  scholarshipProgram?: { scholarship_name?: string; id?: string };
  rfiCase?: string;
  metaApiParams?: Record<string, any>;
  programsOptions: string[];
  defaultCountryCode: string;
  countryList: Array<{ country: string; code: string; countryCode: string }>;
  setApplicationRedirectLoaderDisabled: (value: boolean) => void;
  setRedirectEmail?: (email: string) => void;
  setAPIResposeURL?: (url: string) => void;
  /**
   * When true, PROGRAM_ID always resolves via getProgramId(associatedPrograms)
   * (falls back to the first associated program), matching the pre-refactor
   * NewBannerFormLp behavior. When false/omitted, PROGRAM_ID prefers the
   * explicit ProgramId prop then the field value, matching the pre-refactor
   * organisms/NewBannerForm behavior. The two variants genuinely differed
   * here pre-refactor, so this is preserved as an explicit option rather
   * than silently unified.
   */
  resolveProgramIdFromAssociatedPrograms?: boolean;
  /**
   * webengage.user.login() argument: NewBannerFormLp's original code used
   * resp.ProspectId while organisms/NewBannerForm used resp.ContactId.
   * Preserved per-variant rather than unified.
   */
  webengageLoginIdField?: "ProspectId" | "ContactId";
  /**
   * ConnectModalMba extras: Program Type / program_name on WebEngage and
   * page_url_custom on lead_submit. Banner/LP variants never sent these.
   */
  includeModalAnalyticsExtras?: boolean;
  /**
   * ConnectModalMba form_filled also sent lead_starting_cta. Banner/LP omit it.
   */
  includeLeadStartingCtaOnBlur?: boolean;
  /** When true, country code stays fixed to +91 (corporate brochure RFIs). */
  lockCountryCode?: boolean;
  /**
   * Submit the lead and go straight to the success state, without the OTP
   * step (corporate brochure RFIs). Off by default.
   */
  skipOtp?: boolean;
}

export interface ProgramDetailsOpenRfiViewModel {
  // react-hook-form bindings — single source of truth for values/validation.
  // Views bind fields via <Controller control={control} />; side-effect
  // helpers keep analytics / country / degree→program behavior in sync.
  control: Control<ProgramDetailsOpenRfiFormValues>;
  errors: Partial<Record<keyof ProgramDetailsOpenRfiFormValues, string | undefined>>;
  isSubmitting: boolean;
  isOTPLoading: boolean;
  isFormValid: boolean;
  programsList: string[];
  message: string | null;
  showExistingLead: boolean;
  closeExistingLead: () => void;
  handleFieldChange: (fieldName: keyof ProgramDetailsOpenRfiFormValues, value: string | boolean) => void;
  handleFieldBlur: (fieldName: string, value: string, error: string | undefined) => void;
  updateCountryCode: (value: string) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
  handleFormButtonClick: (e: React.MouseEvent) => void;
  handlePreDegree: () => void;
  isPmi?: boolean;
}

export function useProgramDetailsOpenRfiController(
  options: UseProgramDetailsOpenRfiControllerOptions,
): ProgramDetailsOpenRfiViewModel {
  const {
    ProgramId,
    associatedPrograms,
    isLandingPage = false,
    isAffiliatePage = false,
    isLambdaAPI = false,
    isJobRole = false,
    isScholarShipPage = false,
    scholarshipProgram = {},
    rfiCase,
    metaApiParams,
    programsOptions,
    defaultCountryCode,
    setApplicationRedirectLoaderDisabled,
    setRedirectEmail,
    setAPIResposeURL,
    resolveProgramIdFromAssociatedPrograms = false,
    webengageLoginIdField = "ContactId",
    includeModalAnalyticsExtras = false,
    includeLeadStartingCtaOnBlur = false,
    lockCountryCode = false,
    skipOtp = false,
  } = options;

  const path = usePathname();
  const searchParams = useSearchParams();

  const amsTypeParam = searchParams.get("AMSType");
  const tokenParam = searchParams.get("token") && decryptionJiopc(searchParams.get("token"));

  const jiopcData = useMemo(() => {
    if (amsTypeParam !== "Jiopc" || !tokenParam) return null;
    try {
      const payload = jwtDecode<{ userName?: string }>(tokenParam);
      if (typeof payload?.userName !== "string" || !payload.userName.trim()) return null;
      return { AMSType: amsTypeParam, userName: payload.userName.trim() };
    } catch {
      return null;
    }
  }, [amsTypeParam, tokenParam]);

  const isAfricaPage = path === "/africa";
  const isMbaWomenPage = (path === "/mba-working-women" || path === "/lp-nw/mba-working-women");
  const slug = path?.split("/").pop();
  const isPmi = PMI_ROUTES.includes(slug);
  const { ipStackDetails, countryCode } = useIpStackDetails(isAfricaPage);

  const {
    handleSendOTP,
    isOTPLoading,
    setShowLoader,
    setLeadResponse,
    isHeaderHidden,
    setIsHeaderHidden,
    setIsNewTemplete,
    setFormInitialParams,
    isOTPSent,
  } = useOTPContext();

  // Pre-computed once for ConnectModal analytics (stable for the program page).
  const modalProgramType =
    associatedPrograms[0]?.attributes?.BasicDetails?.ProgramType || "";
  const modalProgramName =
    associatedPrograms[0]?.attributes?.BasicDetails?.Heading || "";

  const [message, setMessage] = useState<string | null>(null);
  const [showExistingLead, setShowExistingLead] = useState(false);
  const isFiredOnceIpinfo = useRef(false);
  const specializationKey = useRef<string>("");
  const custom10PageTypeKey = useRef<string>("");

  const programListFinal = useMemo(
    () => (isLandingPage ? precedentSelectedPrograms(associatedPrograms) : programsOptions),
    [isLandingPage, associatedPrograms, programsOptions],
  );

  const defaultValues: ProgramDetailsOpenRfiFormValues = {
    fullName: "",
    countryCode: defaultCountryCode,
    phoneNumber: "",
    emailId: "",
    degree: SELECT_DEGREE,
    program: SELECT_PROGRAM,
    pmiPathway: SELECT_PMI_PATHWAY,
    programId: ProgramId,
    isTermsAccepted: true,
  };

  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);

  const resolver = useMemo(
    () => createProgramDetailsOpenRfiResolver(isLandingPage, isPmi),
    [isLandingPage, isPmi],
  );

  const {
    control,
    setValue,
    clearErrors,
    getValues,
    trigger,
    handleSubmit,
    formState: { errors: rhfErrors, isSubmitting },
  } = useForm<ProgramDetailsOpenRfiFormValues>({
    defaultValues,
    // Keep submit-mode for untouched fields. Live errors still come from
    // setLeadFormInputValue → trigger(field), so other RFIs stay quiet on open.
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
    isHeaderHidden,
  });

  // Submit busy is kept through lead handoff so the button stays loading until
  // OTP UI mounts. Clear it once OTP is shown, and again on Edit Number
  // (isOTPSent → false), otherwise Download Brochure / RFI shows a stuck loader.
  useEffect(() => {
    setIsSubmitUIBusy(false);
  }, [isOTPSent]);

  // Narrow useWatch subscriptions for side-effect effects only. Field values
  // in the view come from per-field <Controller> render props.
  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedEmailId = useWatch({ control, name: "emailId" });
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedDegree = useWatch({ control, name: "degree" });
  const watchedProgram = useWatch({ control, name: "program" });
  const watchedPmiPathway = useWatch({ control, name: "pmiPathway" });
  const watchedIsTermsAccepted = useWatch({ control, name: "isTermsAccepted" });

  // Cheap regex — not formState.isValid (that re-runs the full resolver each keystroke / INP).
  const isFormValid = useMemo(() => {
    const phoneRegex =
      watchedCountryCode === "+91"
        ? regexForInputs.phoneNumberIndia
        : regexForInputs.phoneNumberIntl;
    const nameOk = regexForInputs.fullName.test(watchedFullName || "");
    const countryOk = regexForInputs.countryCode.test(watchedCountryCode || "");
    const phoneOk = phoneRegex.test(String(watchedPhoneNumber || ""));
    const emailOk = regexForInputs.emailId.test(watchedEmailId || "");
    const termsOk = watchedIsTermsAccepted === true;
    if (!nameOk || !countryOk || !phoneOk || !emailOk || !termsOk) {
      return false;
    }
    if (isPmi && (!watchedPmiPathway || watchedPmiPathway === SELECT_PMI_PATHWAY)) {
      return false;
    }
    if (!isLandingPage) {
      return true;
    }
    return (
      !!watchedDegree &&
      watchedDegree !== SELECT_DEGREE &&
      !!watchedProgram &&
      watchedProgram !== SELECT_PROGRAM
    );
  }, [
    isLandingPage,
    isPmi,
    watchedCountryCode,
    watchedDegree,
    watchedEmailId,
    watchedFullName,
    watchedIsTermsAccepted,
    watchedPhoneNumber,
    watchedPmiPathway,
    watchedProgram,
  ]);

  // Button loading for INP: local busy paints before RHF resolver / error
  // render (empty submits never flip isSubmitting long enough to help).
  const isLoading = isSubmitUIBusy || isSubmitting;

  const leadCheckOnCompletion = useCallback(async () => {
    const values = getValues();
    await checkExistingLead({
      phoneNumber: values.phoneNumber,
      emailId: values.emailId,
      fullName: values.fullName,
      countryCode: values.countryCode,
      uniqueId: getOrCreateUniqueLeadId(),
      commConsentDate: formatDate(new Date()),
    });
  }, [getValues]);

  // Debounced phone lead-check — cancels the previous timer on every change,
  // matches prior behavior (2s debounce, valid phone only).
  useEffect(() => {
    const countryCodeVal = watchedCountryCode;
    const phoneVal = watchedPhoneNumber;
    const regexToUse =
      countryCodeVal === "+91" ? regexForInputs.phoneNumberIndia : regexForInputs.phoneNumberIntl;
    if (!regexToUse.test(phoneVal)) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedPhoneNumber, watchedCountryCode, leadCheckOnCompletion]);

  // Debounced email lead-check.
  useEffect(() => {
    if (!regexForInputs.emailId.test(watchedEmailId)) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedEmailId, leadCheckOnCompletion]);

  // Fire IP-info fetch once, the first time the user starts filling the form.
  useEffect(() => {
    if (
      (watchedFullName || watchedEmailId || watchedPhoneNumber) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [watchedFullName, watchedEmailId, watchedPhoneNumber]);

  // Sync country code from IP-stack detection unless the affiliate page's
  // fixed default should stand, or the caller locked country code (corporate).
  useEffect(() => {
    if (!isAffiliatePage && !lockCountryCode) {
      setValue("countryCode", countryCode);
    }
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (lockCountryCode) {
      setValue("countryCode", defaultCountryCode || "+91");
    }
  }, [lockCountryCode, defaultCountryCode, setValue]);

  useEffect(() => {
    setIsNewTemplete(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const programsList = useMemo(() => {
    if (!isLandingPage) return programListFinal;
    return precedentSelectedPrograms(
      filterSelectedPrograms({ associatedPrograms, degree: watchedDegree }),
    );
  }, [isLandingPage, associatedPrograms, watchedDegree, programListFinal]);

  const handleFieldChange = useCallback(
    (fieldName: keyof ProgramDetailsOpenRfiFormValues, value: string | boolean) => {
      if (lockCountryCode && fieldName === "countryCode") return;

      if (fieldName === "degree" && typeof value === "string") {
        setLeadFormDegreeValue(setValue, clearErrors, value, SELECT_PROGRAM);
        if (isLandingPage) {
          scheduleAfterPaint(() => {
            void trigger("degree");
          });
        }
        return;
      }

      if (fieldName === "program" && typeof value === "string") {
        setLeadFormProgramValue(setValue, clearErrors, value, SELECT_PROGRAM);
        if (isLandingPage) {
          const selectedProgram = associatedPrograms.find(
            (obj) => obj.attributes.BasicDetails?.Heading?.toLocaleUpperCase() === value,
          );
          if (selectedProgram) {
            specializationKey.current = selectedProgram.attributes.BasicDetails?.SpecializationText || "";
            custom10PageTypeKey.current = selectedProgram.attributes.BasicDetails?.Custom10PgType || "";
            setValue("programId", selectedProgram.attributes.BasicDetails?.PROGRAM_ID);
          }
          scheduleAfterPaint(() => {
            void trigger("program");
          });
        }
        return;
      }

      setLeadFormInputValue(setValue, trigger, fieldName, value as any);

      if (fieldName === "countryCode") {
        scheduleAfterPaint(() => {
          void trigger("phoneNumber");
        });
      }
    },
    [associatedPrograms, clearErrors, isLandingPage, lockCountryCode, setValue, trigger],
  );

  const updateCountryCode = useCallback(
    (value: string) => {
      if (lockCountryCode) return;
      setValue("countryCode", value, { shouldDirty: true, shouldValidate: false });
      setValue("phoneNumber", "", { shouldDirty: true, shouldValidate: false });
      scheduleAfterPaint(() => {
        void trigger("phoneNumber");
      });
    },
    [setValue, trigger, lockCountryCode],
  );

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string, error: string | undefined) => {
      pushToDataLayerDeferred({
        event: "form_filled",
        field_name: fieldName,
        option_selected: value || "NA",
        error_message: error || "NA",
        ...(includeLeadStartingCtaOnBlur && {
          lead_starting_cta: getGlobalStateValue("leadStartingCta") || "NA",
        }),
        section_name: getGlobalStateValue("parentSection") || "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });
    },
    [includeLeadStartingCtaOnBlur],
  );

  const handlePreDegree = useCallback(() => {
    const alreadySet = StorageService.get("timeConnectModal");
    if (!alreadySet) {
      StorageService.set("timeConnectModal", true);
    }
  }, []);

  const closeExistingLead = useCallback(() => setShowExistingLead(false), []);

  const leadEndpoint: LeadCreateEndpoint = isLambdaAPI ? "lead-create-lp-new" : jiopcData ? "jiopc-lead-create-new" : "lead-create-new";

  const submitLead = useCallback(
    async (
      payload: ProgramDetailsOpenRfiFormValues & {
        b2i_partner_type?: string;
        b2i_class_section?: string;
        b2i_steam?: string;
      },
    ) => {
      // Paint already happened in handleFormSubmit (double-rAF). Do not yield
      // again here — start lead/OTP work immediately.

      setRedirectEmail?.(payload.emailId);

      const custom6 = isLandingPage
        ? specializationKey.current || ""
        : getSpecializationValue({ associatedPrograms, program: payload.program });

      const form: Record<string, any> = {
        name: payload.fullName,
        phone: payload.phoneNumber,
        countryCode: payload.countryCode,
        email: payload.emailId,
        PROGRAM_ID: resolveProgramIdFromAssociatedPrograms
          ? getProgramId({ programId: ProgramId, associatedPrograms })
          : ProgramId || payload.programId,
        Custom6: custom6,
        Portal: "Degree",
        consent: true,
        ...(isJobRole && { Lead_temp: "CJR1" }),
        ...(isPmi && { Offering: payload.pmiPathway }),
        ...(payload.b2i_partner_type && { b2i_partner_type: payload.b2i_partner_type }),
        ...(payload.b2i_class_section && { b2i_class_section: payload.b2i_class_section }),
        ...(payload.b2i_steam && { b2i_steam: payload.b2i_steam }),
        ...(isMbaWomenPage && { Offering: "RTW" })
      };

      if (isScholarShipPage && scholarshipProgram) {
        form.scholarship_name = scholarshipProgram.scholarship_name || "";
        form.scholarship_id = scholarshipProgram.id || "";
        if (typeof setFormInitialParams === "function") {
          setFormInitialParams((prev: Record<string, any>) => ({
            ...prev,
            scholarship_name: scholarshipProgram.scholarship_name || "",
            scholarship_id: scholarshipProgram.id || "",
          }));
        }
      }

      const Custom10Value = associatedPrograms[0]?.attributes?.BasicDetails?.Custom10PgType;
      // Match originals: only organisms landing+lambda sent Custom5:"False"
      // and Custom10 from the selected-program key. LP + ConnectModal (+ non-lambda
      // banner) always sent Custom5:"Not Verified" with associatedPrograms[0] Custom10.
      const useLandingLambdaExtras = isLandingPage && isLambdaAPI;
      const tracking = trackingDetails();
      const utmMedium =
        tracking?.utm_medium ||
        (rfiCase === "DOWNLOAD_BROCHURE_CASE" || rfiCase === "DOWNLOAD_BROCHURE_CASE_THANK_YOU"
          ? "download_brochure"
          : "request_a_callback");
      const amsFromMeta = metaApiParams?.AMSID ?? metaApiParams?.amsId;
      const meta = {
        ...tracking,
        utm_medium: utmMedium,
        ...metaApiParams,
        ...(amsFromMeta ? { AMSID: String(amsFromMeta) } : {}),
      };

      const verifyOtpCb = async () => {
        setShowLoader(false);
        setApplicationRedirectLoaderDisabled(false);
      };

      const cb = () =>
        new Promise((resolve, reject) => {
          function getEvent(event: any) {
            StorageService.set("mFitlerKeyDetails", event.detail);
          }

          const scheduleNonCritical = (fn: () => void) => {
            // Match pushToDataLayerDeferred — keep GTM/WebEngage off the
            // interaction/paint path after lead success.
            setTimeout(fn, 0);
          };

          async function performApiCalls() {
            let leadSquareResponse: any;
            try {
              const jiopcFields = jiopcData
              ? { AMSType: jiopcData.AMSType, SubscriberID: jiopcData.userName }
              : {};
      
              leadSquareResponse = await createLead({
                form,
                meta,
                endpoint: leadEndpoint,
                formExtra: {
                  Custom5: useLandingLambdaExtras ? "False" : "Not Verified",
                  Custom10: useLandingLambdaExtras
                    ? custom10PageTypeKey.current || ""
                    : Custom10Value || "",
                  ...jiopcFields
                },
                bodyExtra:
                  rfiCase === "DOWNLOAD_BROCHURE_CASE"
                    ? { mx_Enquery_Source: "Enquire" }
                    : undefined,
              });

              if(jiopcData) {
                leadSquareResponse.success = leadSquareResponse?.status === "Success" ? true : false;
                leadSquareResponse.response = leadSquareResponse?.data?.data;
                delete leadSquareResponse.data;
              }

              if (leadSquareResponse) {
                createProspectActivity(leadSquareResponse.response);
              }

              if (leadSquareResponse?.response?.ID === "0" && isAffiliatePage) {
                setShowExistingLead(true);
                setIsSubmitUIBusy(false);
                resolve(undefined);
                return;
              }

              if (leadSquareResponse?.success) {
                const resp = leadSquareResponse?.response || {};

                StorageService.set("LSQ_ID", resp?.ProspectId);
                document.addEventListener("mfilterit:botDetect", getEvent);

                setAPIResposeURL?.(resp?.URL);

                if (typeof setLeadResponse === "function") {
                  setLeadResponse(
                    normalizeLeadResponseForOtpDisplay(resp) || resp,
                  );
                }

                // Defer GTM off lead-create path — same payloads, lower INP processing.
                scheduleNonCritical(() => {
                  pushToDataLayerDeferred({ LSQ_ID: resp?.ProspectId });
                  pushToDataLayerDeferred({
                    event: "MFilter_OTP_Verified_New",
                    LSQ_ID: resp?.ProspectId,
                  });
                  pushToDataLayerDeferred({
                    event: "lead_submit",
                    cta_text: "Submit",
                    lead_starting_cta: getGlobalStateValue("leadStartingCta") || "NA",
                    lead_starting_url: window.location.href || "NA",
                    lead_id: resp?.ProspectId || "NA",
                    section_name: getGlobalStateValue("parentSection") || "NA",
                    status: "success",
                    error_message: "NA",
                    lead_type: "lead_submit_1",
                    page_type: getGlobalStateValue("pageType"),
                    page_url: window?.location?.href || "NA",
                    ...(includeModalAnalyticsExtras && {
                      page_url_custom: window?.location?.href || "NA",
                    }),
                  });
                });

                // WebEngage stays deferred — heavier and non-blocking for GTM.
                scheduleNonCritical(() => {
                  window?.webengage?.track("Lead Form Filled", {
                    "Student Nationality":
                      payload.countryCode === "+91" ? "National" : "International",
                    "First Name": payload.fullName,
                    "Phone Number": payload.countryCode + payload.phoneNumber,
                    ...(includeModalAnalyticsExtras && {
                      "Program Type": modalProgramType,
                      program_name: modalProgramName,
                    }),
                    "Email ID": payload.emailId,
                    "Form Type": "Apply",
                    "Section Name": getGlobalStateValue("parentSection") || "",
                    "Application URL": resp?.URL || "",
                  });

                  window?.webengage?.user?.login(resp?.[webengageLoginIdField]);
                  window?.webengage?.user?.setAttribute("we_email", payload.emailId);
                  window?.webengage?.user?.setAttribute("we_first_name", payload.fullName);
                  window?.webengage?.user?.setAttribute(
                    "we_phone",
                    payload.countryCode + payload.phoneNumber,
                  );
                });


                if (skipOtp || payload.countryCode !== "+91") {
                  // bypass the OTP for international numbers and skipOtp forms
                  verifyOtpCb();
                }
                // skipOtp makes handleSendOTP return before sending an OTP.
                resolve(skipOtp ? { skipOtp: true } : resp);
                return;
              }
            } catch (error) {
              console.error("LeadSquare API call failed:", error);
              pushToDataLayer({
                event: "lead_submit",
                cta_text: "Submit",
                lead_starting_cta: getGlobalStateValue("leadStartingCta") || "NA",
                lead_starting_url: window.location.href || "NA",
                lead_id: "NA",
                section_name: getGlobalStateValue("parentSection") || "NA",
                status: "fail",
                error_message: "LeadSquare API call failed.",
                lead_type: "lead_submit_1",
                page_type: getGlobalStateValue("pageType"),
                page_url: window?.location?.href || "NA",
              });
              setShowLoader(false);
              setIsHeaderHidden(true);
              setIsSubmitUIBusy(false);
              resolve(leadSquareResponse?.response);
              return;
            }

            if (!leadSquareResponse?.success) {
              setShowLoader(false);
              setIsHeaderHidden(true);
              setIsSubmitUIBusy(false);
            }
            resolve(leadSquareResponse?.response);
          }

          performApiCalls().catch((error) => {
            console.error("Error:", error);
            const errorMessage = "Error occurred.";
            pushToDataLayer({
              event: "lead_submit",
              cta_text: "Submit",
              lead_starting_cta: "NA",
              lead_starting_url: window.location.href || "NA",
              lead_id: "NA",
              section_name: getGlobalStateValue("parentSection") || "NA",
              status: "fail",
              error_message: errorMessage,
              lead_type: "lead_submit_1",
              page_type: getGlobalStateValue("pageType"),
              page_url: window?.location?.href || "NA",
            });
            setMessage(errorMessage);
            setIsSubmitUIBusy(false);
            reject(error);
          });
        });

      const checkLeadFail = (_status: boolean) => {
        // todo — preserved from pre-refactor implementation (no-op)
      };

      await handleSendOTP(form, cb, null, verifyOtpCb, checkLeadFail);
    },
    [
      ProgramId,
      associatedPrograms,
      includeModalAnalyticsExtras,
      isAffiliatePage,
      isJobRole,
      isLambdaAPI,
      isLandingPage,
      isScholarShipPage,
      leadEndpoint,
      metaApiParams,
      modalProgramName,
      modalProgramType,
      resolveProgramIdFromAssociatedPrograms,
      rfiCase,
      scholarshipProgram,
      setAPIResposeURL,
      setApplicationRedirectLoaderDisabled,
      setFormInitialParams,
      setIsHeaderHidden,
      setLeadResponse,
      setRedirectEmail,
      webengageLoginIdField,
      setShowLoader,
      skipOtp,
      handleSendOTP,
    ],
  );

  // Empty-submit INP: busy UI paints before resolver; invalid clears busy
  // in onInvalid. Valid submit keeps busy until OTP/fail.
  // Use handleFormButtonClick (type="button") on LP pages to avoid native
  // form submit — GTM attaches #document.onsubmit (~200ms+ blocking).
  const runValidatedSubmit = useCallback(async () => {
    if (isSubmitUIBusy || isSubmitting) return;

    try {
      await import("@/helpers/safeSubmit").then((m) =>
        m.submitAfterValidation({
          rhfHandleSubmit: handleSubmit,
          startBusy: () => setIsSubmitUIBusy(true),
          onValid: async (values) => {
            if (!values.fullName?.trim() || !values.phoneNumber?.trim() || !values.emailId?.trim()) {
              setIsSubmitUIBusy(false);
              return;
            }
            // Feature: banner/LP RFI — defer lead+OTP API after busy paint (INP).
            // Impact: createLead/connectCounsellor no longer extend the submit Interaction turn.
            setTimeout(() => {
              void submitLead(values);
            }, 0);
            // Keep busy after valid handoff — OTP UI / isOTPLoading takes over.
          },
          onInvalid: () => {
            // Invalid: show errors, re-enable button.
            setIsSubmitUIBusy(false);
          },
        }),
      );
    } catch (err) {
      setIsSubmitUIBusy(false);
      throw err;
    }
  }, [handleSubmit, isSubmitUIBusy, isSubmitting, submitLead]);

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      await runValidatedSubmit();
    },
    [runValidatedSubmit],
  );

  const handleFormButtonClick = useCallback(
    async (e: React.MouseEvent) => {
      e?.preventDefault?.();
      e?.stopPropagation?.();
      await runValidatedSubmit();
    },
    [runValidatedSubmit],
  );

  const errors = {
      fullName: rhfErrors.fullName?.message,
      countryCode: rhfErrors.countryCode?.message,
      phoneNumber: rhfErrors.phoneNumber?.message,
      emailId: rhfErrors.emailId?.message,
      degree: rhfErrors.degree?.message,
      program: rhfErrors.program?.message,
      pmiPathway: rhfErrors.pmiPathway?.message,
      isTermsAccepted: rhfErrors.isTermsAccepted?.message,
    };

  return {
    control,
    errors,
    isSubmitting: isLoading,
    isOTPLoading,
    isFormValid,
    programsList,
    message,
    showExistingLead,
    closeExistingLead,
    handleFieldChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
    handleFormButtonClick,
    handlePreDegree,
    isPmi
  };
}
